import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { getUserState, getIndividualRatingState } from "../../../app/selector";
import {
  loadPhotosRandom,
  setCurrentIndex,
  removePhoto
} from "../../../features/individualRating/individualRatingSlice";
import { Row, Col, Carousel, Image, Button, Modal } from "react-bootstrap";
import { ArrowClockwise } from "react-bootstrap-icons";
import PhotoThumbnail from "pages/common/photo/PhotoThumbnail";
import { ReactComponent as Next } from "../../../resources/caret-right-fill.svg";
import { ReactComponent as Prev } from "../../../resources/caret-left-fill.svg";
import { ReactComponent as Do } from "../../../resources/do.svg";
import { ReactComponent as DoNot } from "../../../resources/do-not.svg";
import { BACKEND_URL } from "constants";
import MessagePanel from "pages/common/popover/MessagePanel";
import UploadButton from "pages/common/button/UploadButton";

const profilePicturePlaceholder = require("../../../resources/ProfilePicturePlaceholder.jpg");

const IndividualRating = () => {
  const uploadButtonWrapperRef = useRef(null);
  const dispatch = useDispatch();

  const { user } = useSelector(getUserState);
  const { photos, photoLoaded, index } = useSelector(getIndividualRatingState);

  const [ratingButton, setRatingButton] = useState("");
  const [showNoPictureModal, setShowNoPictureModal] = useState(false);
  const [showMessagePanel, setShowMessagePanel] = useState(false);

  const numOfPhotos = photos ? photos.length : 0;

  const reloadRandomPhoto = useCallback(() => {
    if (!user) {
      console.error("User is undefined");
      return;
    }

    const payload = {
      user_id: user.user_id,
      photo_id_list: photos && photos.length > 0 ? photos.map((p) => p.photo_id) : null,
    };

    axios
      .post(BACKEND_URL + "/random-photos", payload)
      .then((response) => {
        dispatch(loadPhotosRandom({ photos: response.data }));
        setRatingButton("");
        setShowMessagePanel(false);
      })
      .catch((e) => console.error(e));
  }, [user, photos, dispatch]);

  const getRating = useCallback(
    (photo) => {
      if (!photo) return;

      const payload = {
        receiver_user_id: photo.user_id,
        receiver_photo_id: photo.photo_id,
      };

      axios
        .post(BACKEND_URL + "/get-rating", payload)
        .then((response) => {
          const ratingType = response.data?.type || "";

          if (ratingType === "no_picture") {
            setShowNoPictureModal(true);
            setShowMessagePanel(false);
          } else if (ratingType === "do" || ratingType === "would") {
            setShowMessagePanel(true);
          } else {
            setShowMessagePanel(false);
          }

          setRatingButton(ratingType);
        })
        .catch((e) => {
          console.error(e);
          setShowMessagePanel(false);
          dispatch(removePhoto({ index }));
        });
    },
    [index, dispatch]
  );

  useEffect(() => {
    if (!photoLoaded && user) reloadRandomPhoto();
  }, [photoLoaded, reloadRandomPhoto, user]);

  useEffect(() => {
    if (photos && photos[index]) {
      getRating(photos[index]);
    } else {
      setRatingButton("");
      setShowMessagePanel(false);
    }
  }, [photos, index, getRating]);

  const handleSelect = (selectedIndex) => {
    dispatch(setCurrentIndex({ currentIndex: selectedIndex }));
    setShowMessagePanel(false);
  };

  const requestRating = (rating) => {
    if (rating === "no_picture") {
      setShowNoPictureModal(true);
      return;
    }

    const photo = photos[index];
    if (!photo || !user) return;

    const payload = {
      sender_user_id: user.user_id,
      sender_photo_id: "",
      receiver_user_id: photo.user_id,
      receiver_photo_id: photo.photo_id,
      accepted: true,
      origin: "individual_rating",
      type: rating,
    };

    axios
      .post(BACKEND_URL + "/request/create", payload)
      .then(() => {
        if (rating === "not") {
          setShowMessagePanel(false);
          dispatch(removePhoto({ index }));
        } else {
          setRatingButton(rating);
          setShowMessagePanel(true);
        }
      })
      .catch((error) => console.error(error));
  };

  const handleUploadClick = () => {
    const fileInput = uploadButtonWrapperRef.current?.querySelector(
      "input[type='file']"
    );

    if (fileInput) fileInput.click();
    setShowNoPictureModal(false);
  };

  return (
    <div className="content-block single-rating-content-block bg-light rounded position-relative">
      <Button
        onClick={reloadRandomPhoto}
        variant="outline-primary"
        className="position-absolute top-0 end-0 m-2 d-flex align-items-center justify-content-center refreshButton"
        disabled={ratingButton === "no_picture"}
        aria-label="Refresh photos"
      >
        <ArrowClockwise className="refreshIcon" />
      </Button>

      <Row className="justify-content-center title-row w-100">
        <Col>
          <h1 className="text-center menu-title"> Individual Rating</h1>
        </Col>
      </Row>

      <div className="individual-rating-layout">
        <div className="rating-left-col">
          <div className="single-photo-frame">
            <div className="carousel-area">
              <Carousel
                className="photo-slider"
                activeIndex={index}
                onSelect={handleSelect}
                interval={null}
                nextIcon={
                  <Next width="44" height="44" className="drop-shadow" />
                }
                prevIcon={
                  <Prev width="44" height="44" className="drop-shadow" />
                }
                controls={numOfPhotos > 1}
                indicators={numOfPhotos > 1}
              >
                {numOfPhotos > 0 ? (
                  photos.map((photo, i) => (
                    <Carousel.Item key={photo.photo_id || i}>
                      <PhotoThumbnail
                        className="single-rating"
                        thumbnailClass="carousel"
                        src={`data:image/jpeg;base64,${photo.photo}`}
                        photoId={photo.photo_id}
                        fetchHighResOnMount={true}
                      />
                    </Carousel.Item>
                  ))
                ) : (
                  <Carousel.Item key="placeholder">
                    <Image
                      className="single-rating carousel"
                      src={profilePicturePlaceholder}
                      alt="ProfilePicture"
                    />
                  </Carousel.Item>
                )}
              </Carousel>
            </div>

            <div className="rating-button-column">
              <Button
                variant={ratingButton === "do" ? "success" : "outline-success"}
                className="round-button btn-margin"
                onClick={() => requestRating("do")}
                disabled={
                  ratingButton === "do" ||
                  numOfPhotos === 0 ||
                  ratingButton === "no_picture"
                }
              >
                <Do width="28" height="28" />
              </Button>

              <Button
                variant={
                  ratingButton === "would" ? "primary" : "outline-primary"
                }
                className="round-button btn-margin"
                onClick={() => requestRating("would")}
                disabled={
                  ratingButton === "would" ||
                  ratingButton === "do" ||
                  numOfPhotos === 0 ||
                  ratingButton === "no_picture"
                }
              >
                <Do width="28" height="28" />
              </Button>

              <Button
                variant={ratingButton === "not" ? "danger" : "outline-danger"}
                className="round-button btn-margin"
                onClick={() => requestRating("not")}
                disabled={
                  ratingButton === "not" ||
                  numOfPhotos === 0 ||
                  ratingButton === "no_picture"
                }
              >
                <DoNot width="28" height="28" />
              </Button>
            </div>
          </div>
        </div>

        <div className="rating-message-col">
          {showMessagePanel && photos && photos[index] ? (
            <MessagePanel
              receiver_user_id={photos[index]?.user_id}
              receiver_photo_id={photos[index]?.photo_id}
              allowShowProfile
              setShow={setShowMessagePanel}
            />
          ) : (
            <div className="message-placeholder-panel">
             
              <p className="message-text">
                Select the
                <span className="icon-btn icon-green">=</span>
                or
                <span className="icon-btn icon-blue">=</span>
                icon to open the message box. Use the
                <span className="icon-btn icon-red">=</span>
                icon to delete a photo.
              </p>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={showNoPictureModal}
        onHide={() => setShowNoPictureModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Profile Photo Required</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          User has to have at least one profile photo in order to unlock this
          function.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowNoPictureModal(false)}
          >
            Close
          </Button>

          <Button variant="primary" onClick={handleUploadClick}>
            Upload
          </Button>
        </Modal.Footer>
      </Modal>

      <div ref={uploadButtonWrapperRef} style={{ display: "none" }}>
        <UploadButton />
      </div>
    </div>
  );
};

export default IndividualRating;